import React, { useState, useEffect } from 'react';

const NAV_LINKS = [
  { label: 'About',      href: '#about'      },
  { label: 'Experience', href: '#experience'  },
  { label: 'Projects',   href: '#projects'    },
  { label: 'Skills',     href: '#skills'      },
  { label: 'Education',  href: '#education'   },
  { label: 'Contact',    href: '#contact'     },
];

export default function Header() {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [activeId,  setActiveId]  = useState('');

  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY;
      const total    = document.body.scrollHeight - window.innerHeight;
      setScrolled(scrolled > 50);
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);

      // Highlight active section
      const ids = NAV_LINKS.map(l => l.href.slice(1));
      for (let i = ids.length - 1; i >= 0; i--) {
        const el = document.getElementById(ids[i]);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveId(ids[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-slate-950/92 backdrop-blur-md border-b border-slate-800/60 shadow-xl shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      {/* Scroll-progress bar */}
      <div
        className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-sky-500 to-sky-400 transition-all duration-100 pointer-events-none"
        style={{ width: `${progress}%` }}
      />

      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">

        {/* Logo */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="group flex items-center gap-2.5"
        >
          <span className="w-9 h-9 rounded-lg bg-sky-500 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-sky-500/30 group-hover:bg-sky-400 transition-colors">
            MI
          </span>
          <span className="hidden sm:block text-slate-100 font-semibold tracking-tight group-hover:text-sky-400 transition-colors text-sm">
            Muhammad Ilyas
          </span>
        </button>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => {
            const id = l.href.slice(1);
            return (
              <a
                key={l.label}
                href={l.href}
                className={`relative px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  activeId === id
                    ? 'text-sky-400'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/60'
                }`}
              >
                {l.label}
                {activeId === id && (
                  <span className="absolute bottom-0.5 left-3 right-3 h-px bg-sky-400 rounded-full" />
                )}
              </a>
            );
          })}
        </nav>

        {/* CV button */}
        <a
          href="/Muhammad_Ilyas_CV.pdf"
          download
          className="hidden md:inline-flex items-center gap-2 text-sky-400 border border-sky-400/40 hover:bg-sky-400/10 hover:border-sky-400/70 text-sm font-medium px-4 py-2 rounded-lg transition-all"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Resume
        </a>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMenuOpen(v => !v)}
          className="md:hidden w-9 h-9 flex flex-col items-center justify-center gap-[5px] text-slate-300 rounded-lg hover:bg-slate-800 transition-colors"
          aria-label="Toggle menu"
        >
          <span className={`block w-5 h-0.5 bg-current transition-all duration-200 ${menuOpen ? 'rotate-45 translate-y-[7px]' : ''}`} />
          <span className={`block w-5 h-0.5 bg-current transition-all duration-200 ${menuOpen ? 'opacity-0 scale-x-0' : ''}`} />
          <span className={`block w-5 h-0.5 bg-current transition-all duration-200 ${menuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`} />
        </button>
      </div>

      {/* Mobile full-screen menu */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-slate-950 flex flex-col">
          {/* Top bar — mirrors header height so close button aligns with hamburger */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 shrink-0">
            <button
              onClick={() => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="flex items-center gap-2.5"
            >
              <span className="w-9 h-9 rounded-lg bg-sky-500 flex items-center justify-center text-white font-bold text-sm">
                MI
              </span>
              <span className="text-slate-100 font-semibold text-sm">Muhammad Ilyas</span>
            </button>
            <button
              onClick={() => setMenuOpen(false)}
              className="w-9 h-9 flex items-center justify-center text-slate-300 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
              aria-label="Close menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Nav links */}
          <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
            {NAV_LINKS.map((l) => {
              const id = l.href.slice(1);
              return (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className={`text-2xl font-bold py-3 transition-colors ${
                    activeId === id ? 'text-sky-400' : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {l.label}
                </a>
              );
            })}
          </nav>

          {/* Bottom CTA */}
          <div className="px-8 pb-10 shrink-0">
            <a
              href="/Muhammad_Ilyas_CV.pdf"
              download
              className="flex items-center justify-center gap-2 w-full bg-sky-500 hover:bg-sky-400 text-white font-semibold py-3.5 rounded-xl transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Resume
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
